import { z } from 'zod'

import { dateUtil, isNull } from '../../util'

export const millsheetSubMaterialCsvColumnMapper = {
  manufacturerName: 'メーカー名',
  no: '発行No.',
  issuedDate: '発行日',
  constructionName: '工事名称',
  deliveryDestination: '納入先',
  productName: '品名',
  serialNumber: '製造番号',
  quantity: '個数',
  notificationNo: '通知番号',
}

export function validateAndNormalizeMillsheetSubMaterialCsvRecords({
  record,
  index,
}: {
  record: Record<string, string | number | null>
  index: number
}) {
  // すべて空行だったらスキップ
  if (
    Object.values(record).every((x) => x === '')
  ) {
    return {
      type: 'submaterial' as const,
      success: false as const,
      isEmptyLine: true,
      message: `空行です。`,
      record,
      index,
    }
  }

  // TODO: keysのvalidation

  // 必要なデータのみを一旦抽出する
  const data = {
    // ミルシート向け
    manufacturerName: record[millsheetSubMaterialCsvColumnMapper.manufacturerName],
    no: record[millsheetSubMaterialCsvColumnMapper.no],
    issuedDate: record[millsheetSubMaterialCsvColumnMapper.issuedDate],
    constructionName: record[millsheetSubMaterialCsvColumnMapper.constructionName],
    deliveryDestination: record[millsheetSubMaterialCsvColumnMapper.deliveryDestination],
    notificationNo: record[millsheetSubMaterialCsvColumnMapper.notificationNo],
    // ミルシート明細向け
    productName: record[millsheetSubMaterialCsvColumnMapper.productName],
    serialNumber: record[millsheetSubMaterialCsvColumnMapper.serialNumber],
    quantity: record[millsheetSubMaterialCsvColumnMapper.quantity],
  }
  // schemaもハードコーディング
  const schema = z.object({
    manufacturerName: z.string(),
    no: z.string(),
    issuedDate: z.string(),
    constructionName: z.string(),
    deliveryDestination: z.string(),
    notificationNo: z.string().nullish(),
    // ミルシート明細向け
    productName: z.string(),
    serialNumber: z.string(),
    // quantityは数値の文字列（カンマ、小数点を許す）
    quantity: z
      .string()
      .regex(/^[0-9,.]+$/, { message: '数値で入力してください。' })
      .transform((x) => {
        return Number(x.replace(/,/g, ''))
      }),
  })
  const parsed = schema.safeParse(data)

  if (!parsed.success) {
    return {
      type: 'submaterial' as const,
      success: false as const,
      message: parsed.error.issues
        .map((x) => {
          // ネストや refinement 由来の path は mapper のキーに存在しないため、パス文字列にフォールバックする
          const columnLabels: Partial<Record<string, string>> = millsheetSubMaterialCsvColumnMapper
          const label = columnLabels[x.path.join('')] ?? x.path.join('')
          const value = data[x.path.join('') as keyof typeof data]
          return `${label}: ${x.message}(入力された値: ${value})`
        })
        .join('。'),
      record,
      index,
    }
  }

  const dateParsed = dateUtil.parseDateFormatString(parsed.data.issuedDate)
  if (isNull(dateParsed)) {
    return {
      type: 'submaterial' as const,
      success: false as const,
      message: `日付形式がYYYY/MM/DDの形式になっていません。入力：${parsed.data.issuedDate}`,
      record,
      index,
    }
  }
  const result = {
    type: 'submaterial' as const,
    ...parsed.data,
    rank: index + 1,
    issuedDate: dateParsed.date,
  }

  return {
    type: 'submaterial' as const,
    success: true as const,
    record,
    index,
    result,
    resultTitle: result.productName,
  }
}
